import { Head, Link, router, usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

type ClientSummary = {
  readonly id: number
  readonly display_name: string
  readonly slug: string
  readonly color_hex: string
  readonly enabled: boolean
  readonly smartlead_campaign_keywords: readonly string[]
  readonly reply_count: number
}

type ClientsIndexProps = {
  readonly clients: readonly ClientSummary[]
}

export default function ClientsIndex({ clients }: ClientsIndexProps) {
  const { props } = usePage<PageProps<ClientsIndexProps>>()
  const description =
    "Manage ReplyOps client records, campaign matching keywords, voice, prompts, signatures, and enabled state."

  return (
    <>
      <Head title="Clients">
        <meta name="description" content={description} />
        <meta property="og:title" content="Clients" />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>Clients</h1>
              <p className="mt-2">Configure client-specific reply context.</p>
            </div>
            <Button asChild>
              <Link href="/settings/clients/new">
                <Plus className="h-4 w-4" />
                New client
              </Link>
            </Button>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          {clients.length > 0 ? (
            <section className="overflow-hidden rounded-lg border border-hairline bg-page">
              <div className="divide-y divide-hairline">
                {clients.map((client) => (
                  <article
                    key={client.id}
                    className="grid gap-4 px-4 py-4 lg:grid-cols-[minmax(220px,1fr)_1fr_120px_auto]"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full border border-hairline"
                          style={{ backgroundColor: client.color_hex }}
                        />
                        <span className="truncate text-ink-display">
                          {client.display_name}
                        </span>
                        <Badge tone={client.enabled ? "signal" : "muted"}>
                          {client.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      <div className="mt-1 text-sm text-ink-muted">{client.slug}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {client.smartlead_campaign_keywords.length > 0 ? (
                        client.smartlead_campaign_keywords.map((keyword) => (
                          <span
                            key={keyword}
                            className="rounded-md border border-hairline bg-surface px-2 py-1 text-xs"
                          >
                            {keyword}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-ink-muted">No keywords</span>
                      )}
                    </div>
                    <div className="text-sm text-ink-muted">
                      {client.reply_count} replies
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button asChild variant="secondary" size="sm">
                        <Link href={`/settings/clients/${client.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          router.delete(`/settings/clients/${client.id}`, {
                            preserveScroll: true,
                          })
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : (
            <section className="rounded-lg border border-hairline bg-surface px-6 py-12 text-center">
              <h2>No clients configured</h2>
              <p className="mt-2">Create a client to map campaigns and drafting context.</p>
            </section>
          )}
        </div>
      </SettingsLayout>
    </>
  )
}
